/**
 * 💪📊 Hero Biometrics Card — daily body-stat log + trend snapshot.
 *
 * Mobile-first: 6 fast fields (weight, resting HR, HRV, sleep, waist,
 * mood) that a user can fill in about 30 seconds on Android.
 *
 * Backed by:
 *   POST /api/hero/biometrics             — merge today's entry
 *   GET  /api/hero/biometrics/summary     — last-N-days snapshot
 *   GET  /api/hero/biometrics?days=30     — full series (for sparkline)
 *   GET  /api/hero/google-health/status   — is Google Health wired?
 *   GET  /api/hero/google-health/connect  — start OAuth
 *
 * Renders inside the Gym page (drop-in card).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import BiometricTrendChart from "./BiometricTrendChart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_ICONS = { great: "🔥", good: "🙂", meh: "😐", rough: "😩", sick: "🤒" };

function Field({ label, k, unit, value, step, min, max, onChange, testid }) {
  return (
    <label className="flex flex-col text-xs">
      <span className="uppercase tracking-wider text-neutral-400 mb-1">
        {label}
      </span>
      <div className="relative">
        <input
          type="number"
          step={step}
          min={min}
          max={max}
          inputMode="decimal"
          value={value ?? ""}
          onChange={(e) => onChange(k, e.target.value === "" ? null : Number(e.target.value))}
          placeholder="—"
          className="w-full px-2 py-2 rounded-lg bg-black/40 border border-neutral-800 text-neutral-100 text-sm focus:border-amber-500/60 focus:outline-none"
          data-testid={testid}
        />
        {unit && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase text-neutral-500 pointer-events-none">
            {unit}
          </span>
        )}
      </div>
    </label>
  );
}

function Delta({ delta, unit = "", invert = false }) {
  if (delta == null || delta === 0) return null;
  const up = delta > 0;
  const good = invert ? !up : up;
  return (
    <span className={`ml-1 text-[10px] ${good ? "text-emerald-400" : "text-amber-400"}`}>
      {up ? "▲" : "▼"} {Math.abs(delta)}{unit}
    </span>
  );
}

function Sparkline({ series, color = "#F59E0B" }) {
  const vals = series.map((v) => Number(v) || 0);
  if (vals.length < 2) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const w = 120, h = 24, pad = 2;
  const norm = (v) => (max === min ? 0.5 : (v - min) / (max - min));
  const pts = vals.map((v, i) => {
    const x = pad + (i * (w - 2 * pad)) / (vals.length - 1);
    const y = h - pad - norm(v) * (h - 2 * pad);
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={w} height={h} className="mt-1">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} />
    </svg>
  );
}

export default function HeroBiometricsCard() {
  const [summary, setSummary] = useState(null);
  const [today, setToday] = useState({});
  const [series, setSeries] = useState([]);
  const [ghStatus, setGhStatus] = useState({ configured: false, connected: false });
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [stepGoal, setStepGoal] = useState(null);          // server truth
  const [goalDraft, setGoalDraft] = useState("");          // input state
  const [savingGoal, setSavingGoal] = useState(false);

  const refresh = async () => {
    try {
      const [s, t, r, gh, sg] = await Promise.all([
        axios.get(`${API}/hero/biometrics/summary`),
        axios.get(`${API}/hero/biometrics/today`),
        axios.get(`${API}/hero/biometrics?days=30`),
        axios.get(`${API}/hero/google-health/status`).catch(() => ({ data: {} })),
        axios.get(`${API}/hero/step-goal`).catch(() => ({ data: null })),
      ]);
      setSummary(s.data);
      setToday(t.data || {});
      setSeries(r.data?.entries || []);
      setGhStatus(gh.data || {});
      setStepGoal(sg.data || null);
      // Prefill draft with user's actual saved goal (blank if never set)
      if (sg.data?.daily_step_goal != null) {
        setGoalDraft(String(sg.data.daily_step_goal));
      }
    } catch (e) {
      // Silent — card is decorative when API is down.
    }
  };
  useEffect(() => { refresh(); }, []);

  const patch = (k, v) => setToday((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        weight_kg:    today.weight_kg,
        resting_hr:   today.resting_hr,
        hrv_ms:       today.hrv_ms,
        sleep_hours:  today.sleep_hours,
        waist_cm:     today.waist_cm,
        bp_systolic:  today.bp_systolic,
        bp_diastolic: today.bp_diastolic,
        steps:        today.steps,
        mood:         today.mood,
        note:         today.note,
      };
      await axios.post(`${API}/hero/biometrics`, body);
      toast.success("Biometrics logged. Streak counted.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const saveGoal = async () => {
    setSavingGoal(true);
    try {
      const goal = goalDraft === "" ? null : Number(goalDraft);
      const r = await axios.post(`${API}/hero/step-goal`, { goal });
      setStepGoal(r.data);
      toast.success(
        goal ? `Step goal set to ${goal.toLocaleString()}.`
             : "Step goal cleared — using 10,000 default."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save goal.");
    } finally {
      setSavingGoal(false);
    }
  };

  const connectGoogle = async () => {
    try {
      const r = await axios.get(`${API}/hero/google-health/connect`);
      if (r.data?.auth_url) window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not available yet.");
    }
  };

  const weightSeries    = series.filter((r) => r.weight_kg != null).map((r) => r.weight_kg);
  const rhrSeries       = series.filter((r) => r.resting_hr != null).map((r) => r.resting_hr);
  const sleepSeries     = series.filter((r) => r.sleep_hours != null).map((r) => r.sleep_hours);
  const streak = summary?.log_streak ?? 0;

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#0f0f10] via-[#0a0a0a] to-[#100a05] p-4 mb-6 shadow-[0_0_20px_rgba(245,158,11,0.08)]"
         data-testid="hero-biometrics-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="text-2xl">💪</div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-300/80 font-bold">
              Hero Biometrics
            </div>
            <div className="text-[10px] text-neutral-500">
              {streak > 0
                ? `🔥 ${streak}-day log streak · ${summary?.entries ?? 0} entries in 30 d · tap 💾 Save Today's Log below to add today.`
                : "Fill any fields, then tap 💾 Save Today's Log below. Empty fields keep yesterday's value."}
            </div>
          </div>
        </div>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-[11px] px-2 py-1 rounded-full bg-neutral-800 hover:bg-neutral-700 text-neutral-300"
          data-testid="hero-bio-toggle"
        >
          {collapsed ? "expand" : "collapse"}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Snapshot strip */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <SnapshotCell label="Weight" value={summary.weight_kg?.latest}
                            unit="kg" delta={summary.weight_kg?.delta} invert
                            series={weightSeries} testid="hero-snap-weight" />
              <SnapshotCell label="Rest HR" value={summary.resting_hr?.latest}
                            unit="bpm" delta={summary.resting_hr?.delta} invert
                            series={rhrSeries} testid="hero-snap-rhr" />
              <SnapshotCell label="Sleep" value={summary.sleep_hours?.latest}
                            unit="h" delta={summary.sleep_hours?.delta}
                            series={sleepSeries} testid="hero-snap-sleep" />
              <SnapshotCell label="HRV" value={summary.hrv_ms?.latest}
                            unit="ms" delta={summary.hrv_ms?.delta}
                            testid="hero-snap-hrv" />
            </div>
          )}

          {/* 📈 4-week rolling weight chart with PB markers */}
          <div className="mb-3">
            <BiometricTrendChart entries={series} unit="kg" />
          </div>

          {/* Entry form */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            <Field label="Weight"      k="weight_kg"    unit="kg"  step="0.1" min={30} max={500}
                   value={today.weight_kg}   onChange={patch} testid="hero-in-weight" />
            <Field label="Resting HR"  k="resting_hr"   unit="bpm" step="1"   min={30} max={220}
                   value={today.resting_hr}  onChange={patch} testid="hero-in-rhr" />
            <Field label="HRV rMSSD"   k="hrv_ms"       unit="ms"  step="1"   min={1}  max={500}
                   value={today.hrv_ms}      onChange={patch} testid="hero-in-hrv" />
            <Field label="Sleep"       k="sleep_hours"  unit="h"   step="0.25" min={0} max={16}
                   value={today.sleep_hours} onChange={patch} testid="hero-in-sleep" />
            <Field label="Waist"       k="waist_cm"     unit="cm"  step="0.5" min={40} max={200}
                   value={today.waist_cm}    onChange={patch} testid="hero-in-waist" />
            <Field label="Steps"       k="steps"        unit="ct"  step="100" min={0}  max={200000}
                   value={today.steps}       onChange={patch} testid="hero-in-steps" />
            <div className="flex flex-col text-xs">
              <span className="uppercase tracking-wider text-neutral-400 mb-1">Mood</span>
              <div className="flex gap-1">
                {Object.entries(MOOD_ICONS).map(([m, ico]) => (
                  <button
                    key={m}
                    onClick={() => patch("mood", today.mood === m ? null : m)}
                    className={`flex-1 py-1.5 rounded-lg border text-lg transition ${
                      today.mood === m
                        ? "bg-amber-500/25 border-amber-500/70"
                        : "bg-black/40 border-neutral-800 hover:border-neutral-600"
                    }`}
                    title={m}
                    data-testid={`hero-in-mood-${m}`}
                  >{ico}</button>
                ))}
              </div>
            </div>
          </div>

          {/* 🚶 Daily step goal — user-configurable accessibility control */}
          {stepGoal && (() => {
            const eff  = stepGoal.effective_goal || 10000;
            const pct  = Math.max(0, Math.min(100, stepGoal.progress_pct || 0));
            const hit  = !!stepGoal.goal_hit;
            const set  = stepGoal.daily_step_goal;
            const dirty = String(set ?? "") !== goalDraft.trim();
            return (
              <div className="mb-3 rounded-xl bg-black/30 border border-neutral-800 p-3"
                   data-testid="step-goal-panel">
                <div className="flex items-baseline justify-between mb-1.5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                    🚶 Daily Step Goal
                  </div>
                  <div className={`text-[11px] font-bold ${hit ? "text-emerald-300" : "text-neutral-500"}`}
                       data-testid="step-goal-progress-text">
                    {(stepGoal.steps_today || 0).toLocaleString()}
                    <span className="text-neutral-600"> / </span>
                    {eff.toLocaleString()}
                    {hit && <span className="ml-1">✔</span>}
                  </div>
                </div>
                <div className="h-2 rounded-full bg-neutral-900 overflow-hidden mb-2">
                  <div
                    className={`h-full transition-all ${hit ? "bg-emerald-500" : "bg-amber-500"}`}
                    style={{ width: `${pct}%` }}
                    data-testid="step-goal-progress-bar"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    inputMode="numeric"
                    step="500"
                    min={stepGoal.min_goal || 500}
                    max={stepGoal.max_goal || 100000}
                    value={goalDraft}
                    onChange={(e) => setGoalDraft(e.target.value)}
                    placeholder={`Default ${(stepGoal.default_goal || 10000).toLocaleString()}`}
                    className="flex-1 px-2 py-1.5 rounded-lg bg-black/40 border border-neutral-800 text-neutral-100 text-sm focus:border-amber-500/60 focus:outline-none"
                    data-testid="step-goal-input"
                  />
                  <button
                    onClick={saveGoal}
                    disabled={savingGoal || !dirty}
                    className="text-[11px] px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="step-goal-save"
                  >
                    {savingGoal ? "…" : "Set Goal"}
                  </button>
                  {set != null && (
                    <button
                      onClick={() => { setGoalDraft(""); }}
                      className="text-[11px] px-2 py-1.5 rounded-lg bg-neutral-800 border border-neutral-700 text-neutral-400 hover:bg-neutral-700"
                      data-testid="step-goal-reset"
                      title="Clear your custom goal and fall back to the 10,000 default"
                    >
                      Reset
                    </button>
                  )}
                </div>
                <div className="text-[10px] text-neutral-500 mt-1.5">
                  {set == null
                    ? `Using the default ${(stepGoal.default_goal || 10000).toLocaleString()} — set your own any time.`
                    : `Your custom goal: ${set.toLocaleString()} steps/day.`}
                </div>
              </div>
            );
          })()}

          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600 text-black font-black tracking-wide hover:brightness-110 disabled:opacity-60"
              data-testid="hero-bio-save"
              title="Saves everything above as today's biometric entry"
            >
              {saving ? "Saving…" : "💾 Save Today's Log"}
            </button>
            {ghStatus.configured ? (
              ghStatus.connected ? (
                <span className="text-[11px] px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-200"
                      data-testid="gh-connected-badge">
                  ✅ Google Health connected
                </span>
              ) : (
                <button
                  onClick={connectGoogle}
                  className="text-[11px] px-3 py-2 rounded-lg bg-sky-500/15 border border-sky-500/40 text-sky-200 hover:bg-sky-500/25"
                  data-testid="gh-connect-btn"
                >
                  🩺 Connect Google Health
                </button>
              )
            ) : (
              <span className="text-[10px] px-3 py-2 rounded-lg bg-neutral-900 border border-neutral-800 text-neutral-500"
                    title="Owner: drop GOOGLE_HEALTH_CLIENT_ID / SECRET / REDIRECT_URI into backend/.env to enable auto-sync."
                    data-testid="gh-not-configured">
              🩺 Google Health · setup pending
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SnapshotCell({ label, value, unit, delta, invert = false, series = [], testid }) {
  return (
    <div className="rounded-lg bg-black/40 border border-neutral-800 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="flex items-baseline">
        <div className="text-lg font-black text-amber-300"
             style={{ fontFamily: "Anton, sans-serif" }}>
          {value ?? "—"}
        </div>
        {value != null && unit && (
          <span className="ml-1 text-[10px] text-neutral-500">{unit}</span>
        )}
        <Delta delta={delta} unit={unit} invert={invert} />
      </div>
      {series?.length >= 2 && <Sparkline series={series} />}
    </div>
  );
}
